import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useLocation } from "react-router-dom";
import {
	Button,
	Card,
	Checkbox,
	Col,
	DatePicker,
	Form,
	Input,
	InputNumber,
	Popconfirm,
	Row,
	Select,
	Space,
	Table,
	Typography,
} from "antd";
import dayjs from "dayjs";

import PageTitle from "../../page-header/PageHeader";
import { loadAllProject } from "../../../redux/rtk/features/projectManagement/project/project/project";
import {
	deleteProjectWBS,
	loadProjectWBS,
	saveProjectWBS,
} from "../../../redux/rtk/features/projectManagement/project/projectWBS/projectWBS";

const parseNumber = (value, fallback = null) => {
	if (value === undefined || value === null || value === "") {
		return fallback;
	}
	const numeric = Number(value);
	return Number.isNaN(numeric) ? fallback : numeric;
};

const mapWBSRecord = (record, index = 0) => {
	const projectId = parseNumber(record?.ProjectId ?? record?.projectId, null);
	const wbsId = parseNumber(record?.WBSId ?? record?.wbsId, index);
	const parentId = parseNumber(record?.ParentWBSId ?? record?.parentWBSId, 0);

	return {
		key: `${projectId ?? "p"}-${wbsId ?? index}`,
		WBSId: wbsId,
		ProjectId: projectId,
		ProjectName: record?.ProjectName ?? record?.projectName ?? "",
		ParentWBSId: parentId,
		ParentWBSName: record?.ParentWBSName ?? record?.parentWBSName ?? null,
		WBSName: record?.WBSName ?? record?.wbsName ?? "",
		WBSDescription: record?.WBSDescription ?? record?.wbsDescription ?? "",
		IsMilestone: Boolean(record?.IsMilestone ?? record?.isMilestone),
		StartDate: record?.StartDate ?? record?.startDate ?? null,
		EndDate: record?.EndDate ?? record?.endDate ?? null,
		ProgressPercent: parseNumber(record?.ProgressPercent ?? record?.progressPercent, 0) ?? 0,
		DataStatus: record?.DataStatus ?? record?.dataStatus,
		CreatedDate: record?.CreatedDate ?? record?.createdDate ?? null,
		ModifiedDate: record?.ModifiedDate ?? record?.modifiedDate ?? null,
		CreatedBy: record?.CreatedBy ?? record?.createdBy ?? null,
		ModifiedBy: record?.ModifiedBy ?? record?.modifiedBy ?? null,
	};
};

const ProjectWBSDetails = () => {
	const dispatch = useDispatch();
	const isLogged = Boolean(localStorage.getItem("isLogged"));

	const { list: projectList = [], loading: projectLoading } = useSelector(
		(state) => state.project || {}
	);
	const {
		list: wbsList = [],
		loading: wbsLoading,
	} = useSelector((state) => state.projectWBS || {});

	const [selectedProjectId, setSelectedProjectId] = useState(null);
	const [editingWBS, setEditingWBS] = useState(null);
	const [form] = Form.useForm();
const location = useLocation();

	useEffect(() => {
		dispatch(loadAllProject());
		dispatch(loadProjectWBS());
	}, [dispatch]);

	useEffect(() => {
		if (selectedProjectId) {
			dispatch(loadProjectWBS(parseNumber(selectedProjectId, selectedProjectId)));
		}
	}, [dispatch, selectedProjectId]);

	const projectOptions = useMemo(() => {
		return (Array.isArray(projectList) ? projectList : [])
			.map((project) => {
			const rawValue = project?.ProjectId ?? project?.id;
			const value =
				rawValue !== undefined && rawValue !== null ? String(rawValue) : null;
				const label = project?.ProjectName || project?.name;
			if (value === null || !label) {
					return null;
				}
				return {
					value,
					label,
				};
			})
			.filter(Boolean);
	}, [projectList]);

	const mappedWBSList = useMemo(
		() =>
			Array.isArray(wbsList)
				? wbsList.map((item, index) => mapWBSRecord(item, index))
				: [],
		[wbsList]
	);

	const parentOptions = useMemo(() => {
		const numericSelected = parseNumber(selectedProjectId, selectedProjectId);
		const scopedList =
			numericSelected !== null && numericSelected !== undefined
				? mappedWBSList.filter((item) => parseNumber(item?.ProjectId, item?.ProjectId) === numericSelected)
				: mappedWBSList;
		const baseOptions = [{ value: 0, label: "Root" }];
		return baseOptions.concat(
			scopedList
				.map((item) => {
					const value = parseNumber(item?.WBSId ?? item?.wbsId, null);
					const label = item?.WBSName || (value !== null ? `WBS ${value}` : null);
					if (value === null || !label) {
						return null;
					}
					return {
						value,
						label,
					};
				})
				.filter(Boolean)
		);
	}, [mappedWBSList, selectedProjectId]);

	const resetForm = useCallback(() => {
		const currentProject = selectedProjectId ?? form.getFieldValue("SelectedProject") ?? null;
		setEditingWBS(null);
		form.resetFields([
			"ParentWBSId",
			"WBSName",
			"WBSDescription",
			"IsMilestone",
			"StartDate",
			"EndDate",
			"ProgressPercent",
		]);
		form.setFieldsValue({
			SelectedProject: currentProject,
			ParentWBSId: 0,
			IsMilestone: false,
			ProgressPercent: 0,
			StartDate: null,
			EndDate: null,
		});
	}, [form, selectedProjectId]);

	const handleProjectChange = useCallback(
		(value) => {
			setSelectedProjectId(value);
			setEditingWBS(null);
			form.setFieldsValue({
				SelectedProject: value,
				ParentWBSId: 0,
				IsMilestone: false,
				ProgressPercent: 0,
				StartDate: null,
				EndDate: null,
				WBSName: "",
				WBSDescription: "",
			});
			dispatch(loadProjectWBS(parseNumber(value, value)));
		},
		[dispatch, form]
	);

	const handleEdit = useCallback(
		(record) => {
			setEditingWBS(record);
		const rawProjectId = record?.ProjectId ?? record?.projectId ?? null;
		const projectValue =
			rawProjectId !== null && rawProjectId !== undefined ? String(rawProjectId) : null;
		setSelectedProjectId(projectValue);
			form.setFieldsValue({
			SelectedProject: projectValue,
				WBSName: record?.WBSName || "",
				WBSDescription: record?.WBSDescription || "",
				ParentWBSId: parseNumber(record?.ParentWBSId, 0),
				IsMilestone: Boolean(record?.IsMilestone),
				StartDate: record?.StartDate ? dayjs(record.StartDate) : null,
				EndDate: record?.EndDate ? dayjs(record.EndDate) : null,
				ProgressPercent: parseNumber(record?.ProgressPercent, 0),
			});
		},
	[form]
	);

	const handleDelete = useCallback(
		(record) => {
			if (!selectedProjectId) {
				return;
			}
			const numericProject = parseNumber(selectedProjectId, selectedProjectId);
			dispatch(
				deleteProjectWBS({
					ProjectId: numericProject,
					WBSId: record?.WBSId ?? record?.wbsId,
				})
			).then((res) => {
				if (res?.meta?.requestStatus !== "rejected") {
					dispatch(loadProjectWBS(numericProject));
					resetForm();
				}
			});
		},
		[dispatch, resetForm, selectedProjectId]
	);

	const handleSubmit = useCallback(
		(values) => {
			if (!selectedProjectId) {
				return;
			}
			const numericProject = parseNumber(selectedProjectId, selectedProjectId);
			const payload = {
				WBSId: editingWBS?.WBSId ?? 0,
				ProjectId: numericProject,
				ParentWBSId: parseNumber(values?.ParentWBSId, 0) ?? 0,
				WBSName: values?.WBSName ?? "",
				WBSDescription: values?.WBSDescription ?? "",
				IsMilestone: Boolean(values?.IsMilestone),
				StartDate: values?.StartDate ? dayjs(values.StartDate).format("YYYY-MM-DD") : null,
				EndDate: values?.EndDate ? dayjs(values.EndDate).format("YYYY-MM-DD") : null,
				ProgressPercent: parseNumber(values?.ProgressPercent, 0) ?? 0,
				DataStatus: 1,
			};
			dispatch(saveProjectWBS(payload)).then((res) => {
				if (res?.meta?.requestStatus !== "rejected") {
					dispatch(loadProjectWBS(numericProject));
					resetForm();
				}
			});
		},
		[dispatch, editingWBS, resetForm, selectedProjectId]
	);

	const columns = useMemo(
		() => [
			{
				title: "Project",
				dataIndex: "ProjectName",
				key: "ProjectName",
				render: (_, record) => record.ProjectName || record.ProjectId || "-",
			},
			{
				title: "WBS Name",
				dataIndex: "WBSName",
				key: "WBSName",
			},
			{
				title: "Description",
				dataIndex: "WBSDescription",
				key: "WBSDescription",
			},
			{
				title: "Milestone",
				dataIndex: "IsMilestone",
				key: "IsMilestone",
				render: (value) => (value ? "Yes" : "No"),
			},
			{
				title: "Parent",
				dataIndex: "ParentWBSName",
				key: "ParentWBSName",
				render: (_, record) => {
					if (!record?.ParentWBSId) {
						return "Root";
					}
					return record?.ParentWBSName || `WBS ${record?.ParentWBSId}`;
				},
			},
			{
				title: "Start Date",
				dataIndex: "StartDate",
				key: "StartDate",
				render: (value) => (value ? dayjs(value).format("YYYY-MM-DD") : "-"),
			},
			{
				title: "End Date",
				dataIndex: "EndDate",
				key: "EndDate",
				render: (value) => (value ? dayjs(value).format("YYYY-MM-DD") : "-"),
			},
			{
				title: "Progress %",
				dataIndex: "ProgressPercent",
				key: "ProgressPercent",
				render: (value) => `${parseNumber(value, 0)}%`,
			},
			{
				title: "Actions",
				key: "actions",
				render: (_, record) => (
					<Space>
						<Button type="link" onClick={() => handleEdit(record)}>
							Edit
						</Button>
						<Popconfirm
							title="Delete WBS"
							description={`Are you sure you want to delete ${record?.WBSName || "this WBS"}?`}
							onConfirm={() => handleDelete(record)}
						>
							<Button type="link" danger>
								Delete
							</Button>
						</Popconfirm>
					</Space>
				),
			},
		],
		[handleDelete, handleEdit]
	);

	useEffect(() => {
		const initialProject = location?.state?.projectId;
		if (initialProject) {
		const projectValue = String(initialProject);
		setSelectedProjectId(projectValue);
			form.setFieldsValue({
			SelectedProject: projectValue,
				ParentWBSId: 0,
				IsMilestone: false,
				ProgressPercent: 0,
			});
			dispatch(loadProjectWBS(parseNumber(initialProject, initialProject)));
		}
	}, []);

	if (!isLogged) {
		return <Navigate to="/admin/auth/login" replace />;
	}

	return (
		<div className="wbs-page-container">
			<PageTitle title="Back" />
			<Row gutter={[16, 16]} className="wbs-row">
				<Col xs={24} md={10} lg={8} className="wbs-form-col">
					<Card
						title={<Typography.Title level={4} style={{ margin: 0 }}>Manage WBS</Typography.Title>}
						extra={
							<Button onClick={resetForm} disabled={!selectedProjectId || wbsLoading}>
								New WBS
							</Button>
						}
						className="wbs-form-card"
					>
						<Form form={form} layout="vertical" onFinish={handleSubmit}>
						<Form.Item
							label="Select Project"
							name="SelectedProject"
							rules={[{ required: true, message: "Select a project" }]}
						>
							<Select
								showSearch
								placeholder="Select Project"
								options={projectOptions}
								loading={projectLoading}
								optionFilterProp="label"
								onChange={handleProjectChange}
								value={selectedProjectId}
							/>
						</Form.Item>

						<Form.Item label="Parent WBS" name="ParentWBSId" initialValue={0}>
							<Select
								options={parentOptions}
								disabled={!selectedProjectId}
							/>
						</Form.Item>

						<Form.Item
							label="WBS Name"
							name="WBSName"
							rules={[{ required: true, message: "Enter WBS name" }]}
						>
							<Input placeholder="Enter WBS name" disabled={!selectedProjectId} />
						</Form.Item>

						<Form.Item
							label="Description"
							name="WBSDescription"
							rules={[{ required: true, message: "Enter WBS description" }]}
						>
							<Input.TextArea rows={3} placeholder="Enter description" disabled={!selectedProjectId} />
						</Form.Item>

						<Form.Item name="IsMilestone" valuePropName="checked">
							<Checkbox disabled={!selectedProjectId}>Is Milestone</Checkbox>
						</Form.Item>

						<Form.Item
							label="Start Date"
							name="StartDate"
							rules={[{ required: true, message: "Select start date" }]}
						>
							<DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" disabled={!selectedProjectId} />
						</Form.Item>

						<Form.Item
							label="End Date"
							name="EndDate"
							rules={[{ required: true, message: "Select end date" }]}
						>
							<DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" disabled={!selectedProjectId} />
						</Form.Item>

						<Form.Item
							label="Progress %"
							name="ProgressPercent"
							rules={[{ required: true, message: "Enter progress percent" }]}
						>
							<InputNumber
								style={{ width: "100%" }}
								min={0}
								max={100}
								step={1}
								disabled={!selectedProjectId}
							/>
						</Form.Item>

							<div style={{ textAlign: "right" }}>
								<Button
									type="primary"
									htmlType="submit"
									disabled={!selectedProjectId}
									loading={wbsLoading}
								>
									{editingWBS ? "Update WBS" : "Add WBS"}
								</Button>
							</div>
						</Form>
					</Card>
				</Col>

				<Col xs={24} md={14} lg={16} className="wbs-table-col">
					<div className="ant-card p-4 rounded  wbs-table-container">
						<div className="flex my-2 justify-between">
							<h5 className="text-xl mb-2">WBS List</h5>
						</div>
						{wbsLoading ? (
							<div className="text-center py-8">
								<div className="ant-spin ant-spin-lg ant-spin-spinning">
									<span className="ant-spin-dot ant-spin-dot-spin">
										<i className="ant-spin-dot-item"></i>
										<i className="ant-spin-dot-item"></i>
										<i className="ant-spin-dot-item"></i>
										<i className="ant-spin-dot-item"></i>
									</span>
								</div>
								<p className="mt-4 text-gray-600">Loading WBS...</p>
							</div>
						) : !mappedWBSList || mappedWBSList.length === 0 ? (
							<div className="text-center py-8">
								<p className="text-gray-600">No WBS found.</p>
							</div>
						) : (
							<Table
								dataSource={mappedWBSList.filter((item) => {
									if (!selectedProjectId && selectedProjectId !== 0) {
										return true;
									}
									const numericProject = parseNumber(selectedProjectId, selectedProjectId);
									return parseNumber(item?.ProjectId, item?.ProjectId) === numericProject;
								})}
								columns={columns}
								pagination={{
									defaultPageSize: 20,
								}}
								rowKey={(record) => record?.WBSId ?? record?.wbsId}
								scroll={{ x: true, y: "100%" }}
								className="get-all-user-table"
							/>
						)}
					</div>
				</Col>
			</Row>
		</div>
	);
};

export default ProjectWBSDetails;
